var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
// Intentional @components import: matches the dashboard's qmb-dialog modal, which
// @ui has no equivalent for. Migrate when the dashboard's TableModal moves to @ui.
import TableModal from '@components/TableModal';
import { translate } from '@utils/i18n';
import { getDeviceLegend } from './services/LegendService';
const NAMESPACE = 'alarm_device_legend';
const TRIGGER_SELECTOR = '[data-alarm-legend-trigger]';
function buildColumns(path) {
    const columns = [];
    if (path === 'ulc') {
        columns.push({ dataKey: 'abbrv', label: translate('abbreviation', { namespace: NAMESPACE }) });
    }
    columns.push({ dataKey: 'description', label: translate('device_type', { namespace: NAMESPACE }) }, { dataKey: 'manufacturer', label: translate('manufacturer', { namespace: NAMESPACE }) }, { dataKey: 'model', label: translate('model', { namespace: NAMESPACE }) }, { dataKey: 'activeCount', label: translate('count', { namespace: NAMESPACE }), align: 'right' });
    return columns;
}
function readTarget(trigger) {
    var _a;
    return {
        alarmSystemId: Number(trigger.dataset.alarmSystemId),
        systemName: (_a = trigger.dataset.systemName) !== null && _a !== void 0 ? _a : '',
        deviceCount: Number(trigger.dataset.deviceCount)
    };
}
/**
 * One shared modal per tab (not one React root per row): listens via document
 * delegation for clicks on `[data-alarm-legend-trigger]` buttons and opens the
 * legend for whichever alarm system was clicked.
 */
export function LegendModalView() {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [target, setTarget] = useState(null);
    const [path, setPath] = useState('nfpa');
    const [rows, setRows] = useState([]);
    // Re-opening a viewed system skips the fetch.
    const cache = useRef(new Map());
    // Latest clicked system; a response is applied only while it still matches, so a
    // slow fetch for a since-closed/switched system is ignored.
    const activeSystemId = useRef(null);
    const load = useCallback((alarmSystemId) => __awaiter(this, void 0, void 0, function* () {
        const cached = cache.current.get(alarmSystemId);
        if (cached) {
            setPath(cached.path);
            setRows(cached.rows);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const response = yield getDeviceLegend(alarmSystemId);
            cache.current.set(alarmSystemId, response);
            if (activeSystemId.current !== alarmSystemId)
                return;
            setPath(response.path);
            setRows(response.rows);
        }
        catch (_a) {
            if (activeSystemId.current !== alarmSystemId)
                return;
            setError(true);
        }
        finally {
            if (activeSystemId.current === alarmSystemId)
                setLoading(false);
        }
    }), []);
    useEffect(() => {
        const handleClick = (event) => {
            var _a;
            const trigger = (_a = event.target) === null || _a === void 0 ? void 0 : _a.closest(TRIGGER_SELECTOR);
            if (!trigger)
                return;
            event.preventDefault();
            const clicked = readTarget(trigger);
            activeSystemId.current = clicked.alarmSystemId;
            setTarget(clicked);
            setRows([]);
            setIsOpen(true);
            if (Number.isNaN(clicked.alarmSystemId)) {
                setError(true);
                return;
            }
            setError(false);
            void load(clicked.alarmSystemId);
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [load]);
    const close = () => {
        activeSystemId.current = null;
        setIsOpen(false);
    };
    const title = target
        ? translate('title', {
            namespace: NAMESPACE,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            system_name: target.systemName,
            count: target.deviceCount
        })
        : '';
    return (React.createElement(TableModal, { isOpen: isOpen, onClose: close, loading: loading, title: title, subTitleView: error ? translate('error', { namespace: NAMESPACE }) : null, data: error ? [] : rows, columns: buildColumns(path), className: "qmb-dialog--modal" }));
}
